import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getFlavors } from '../services/api';
import { flavors as mockFlavors } from '../data/mock';

const FlavorsSection = () => {
  const [activeIndex, setActiveIndex] = useState(1);
  const [flavors, setFlavors] = useState(mockFlavors);
  const scrollRef = useRef(null);
  useEffect(() => {
    getFlavors()
      .then(setFlavors)
      .catch(() => setFlavors(mockFlavors));
  }, []);

  useEffect(() => {
    if (scrollRef.current && flavors.length > 0) {
      const el = scrollRef.current;
      const activeEl = el.children[activeIndex];
      if (activeEl) {
        const scrollLeft = activeEl.offsetLeft - (el.clientWidth / 2) + (activeEl.clientWidth / 2);
        el.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [activeIndex, flavors]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -240 : 240, behavior: 'smooth' });
  };

  const activeFlavor = flavors[activeIndex];

  return (
    <section className="py-20 bg-white overflow-hidden">
      {/* Heading */}
      <div className="text-center mb-12 px-4">
        <h2
          className="text-[#0a0a0a] font-black italic mb-4"
          style={{
            fontFamily: 'Nunito, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 4rem)',
          }}
        >
          premium vodka soda
        </h2>
        <p
          className="text-[#0a0a0a] text-base md:text-lg max-w-md mx-auto"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          premium vodka soda
        </p>
        <p
          className="text-gray-500 text-sm md:text-base max-w-md mx-auto mt-2"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          ideală pentru orice plan
        </p>
      </div>

      {/* Flavor Cards Carousel */}
      <div className="relative">
        <button
          onClick={() => scroll('left')}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2 hover:bg-gray-50 transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft size={22} className="text-[#0a0a0a]" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-4 md:gap-8 overflow-x-auto justify-start md:justify-center px-8 md:px-16 pb-8 hide-scrollbar"
          style={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {flavors.map((flavor, idx) => (
            <div
              key={flavor.id}
              className="flex-shrink-0 cursor-pointer"
              style={{ scrollSnapAlign: 'center', width: '280px' }}
              onClick={() => setActiveIndex(idx)}
            >
              <div
                className="w-full rounded-[40px] overflow-hidden flex items-center justify-center p-0 transition-all duration-500"
                style={{
                  background: '#ffffff',
                  height: '420px',
                  transform: activeIndex === idx ? 'scale(1.15)' : 'scale(0.85)',
                  opacity: activeIndex === idx ? 1 : 0.4,
                }}
              >
                <img
                  src={flavor.image}
                  alt={flavor.name}
                  className="h-full w-full object-contain transition-transform duration-500 p-6"
                  style={{
                    filter: activeIndex === idx ? 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))' : 'none'
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => scroll('right')}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2 hover:bg-gray-50 transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight size={22} className="text-[#0a0a0a]" />
        </button>
      </div>

      <div
        className="flex justify-center mt-6 pb-2"
      >
        <div className="flex flex-wrap gap-3 justify-center px-4">
          {flavors.map((flavor, idx) => (
            <button
              key={flavor.id}
              onClick={() => setActiveIndex(idx)}
              className="flex-shrink-0 px-5 py-3 rounded-2xl font-black italic text-sm transition-all duration-200 hover:scale-105"
              style={{
                fontFamily: 'Nunito, sans-serif',
                backgroundColor: flavor.color,
                color: flavor.textColor,
                border: activeIndex === idx ? `2.5px solid ${flavor.textColor}` : '2.5px solid transparent',
                transform: activeIndex === idx ? 'scale(1.05)' : 'scale(1)',
                boxShadow: activeIndex === idx ? `0 4px 16px ${flavor.color}88` : 'none',
              }}
            >
              {flavor.name}
            </button>
          ))}
        </div>
      </div>

      {/* Scroll Progress Bar */}
      <div className="max-w-2xl mx-auto mt-6 px-8">
        <div className="h-0.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#0a0a0a] rounded-full transition-all duration-500"
            style={{ width: `${((activeIndex + 1) / flavors.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Active flavor detail */}
      {activeFlavor && (
        <div className="max-w-xl mx-auto mt-8 px-8 text-center">
          <h3
            className="text-[#0a0a0a] font-black italic text-2xl mb-2"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            {activeFlavor.name}
          </h3>
          <p className="text-gray-600 text-sm mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {activeFlavor.description}
          </p>
          <p
            className="text-[#0a0a0a]/60 text-xs font-extrabold uppercase tracking-widest mb-2"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            DISPONIBIL ÎN:
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            {activeFlavor.availableIn.map((packId) => (
              <Link
                key={packId}
                to={`/products/${packId}`}
                className="text-[#0a0a0a] font-bold text-sm underline underline-offset-2 hover:opacity-70 transition-opacity"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                {packId.replace(/-/g, ' ')}
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default FlavorsSection;
